import * as localForage from 'localforage'
import UniversalStorage from '@vue-storefront/core/store/lib/storage'
import { currentStoreView } from '@vue-storefront/core/lib/multistore'
import rootStore from '@vue-storefront/core/store'
import { IdleActivity } from '../../idle-activity'

const SSO_SESSION_KEY = 'login-as-customer-session'
const SSO_LAST_ACTIVITY_KEY = 'sso-last-activity'
const SSO_IDLE_MINUTES = 60 * 3

let ssoIdle: IdleActivity | null = null
let logoutListenerBound = false

function isSsoSession (): boolean {
  try {
    return window.localStorage.getItem(SSO_SESSION_KEY) === '1'
  } catch (e) {
    return false
  }
}

function markSsoSession (): void {
  window.localStorage.setItem(SSO_SESSION_KEY, '1')
}

function clearSsoSession (): void {
  try {
    window.localStorage.removeItem(SSO_SESSION_KEY)
    window.localStorage.removeItem(SSO_LAST_ACTIVITY_KEY)
  } catch (e) {
    // ignore
  }
}

function stopSsoIdleWatcher (): void {
  ssoIdle && ssoIdle.stop()
  ssoIdle = null
}

function startSsoIdleWatcher (store): void {
  if (!isSsoSession() || ssoIdle) return

  ssoIdle = new IdleActivity()
    .withStorageKey(SSO_LAST_ACTIVITY_KEY)
    .within(SSO_IDLE_MINUTES)
    .whenEnabled(isSsoSession)
    .do(() => {
      stopSsoIdleWatcher()
      clearSsoSession()
      store.dispatch('user/logout', { silent: false })
    })
    .start()
}

function bindLogoutCleanup (Vue): void {
  if (logoutListenerBound) return
  logoutListenerBound = true

  Vue.prototype.$bus.$on('user-after-logout', () => {
    stopSsoIdleWatcher()
    clearSsoSession()
  })
}

export async function bootLoginAsCustomer ({ Vue, config, store, isServer }) {
  if (isServer) return

  bindLogoutCleanup(Vue)

  // get params
  const params = new URLSearchParams(window.location.search)
  const sso = params.get('__sso')
  const sst = params.get('__sst')
  const cartToken = params.get('__ct')

  // Existing session (post-reload): start multi-tab idle timeout
  if (!sso || !sst) {
    if (isSsoSession()) {
      startSsoIdleWatcher(store)
    }
    return
  }

  const storeView = currentStoreView()
  const dbNamePrefix = storeView.storeCode ? storeView.storeCode + '-' : ''

  // clear local storage
  window.localStorage.clear()

  // rebuild local storage with new tokens
  Vue.prototype.$db.cartsCollection = new UniversalStorage(localForage.createInstance({
    name: (config.storeViews.commonCache ? '' : dbNamePrefix) + 'shop',
    storeName: 'carts',
    driver: localForage[config.localForage.defaultDrivers['carts']]
  }))

  Vue.prototype.$db.usersCollection = new UniversalStorage(localForage.createInstance({
    name: (config.storeViews.commonCache ? '' : dbNamePrefix) + 'shop',
    storeName: 'user',
    driver: localForage[config.localForage.defaultDrivers['user']]
  }))

  if (cartToken) {
    await Vue.prototype.$db.cartsCollection.setItem('current-cart-token', cartToken)
  }

  await Vue.prototype.$db.usersCollection.setItem('current-token', sso)
  await Vue.prototype.$db.usersCollection.setItem('current-refresh-token', sst)

  // Mark SSO session after clear so idle watcher can resume post-reload
  markSsoSession()

  const onSsoLoggedIn = () => {
    Vue.prototype.$bus.$off('user-after-loggedin', onSsoLoggedIn)
    // Strip SSO query params; cache-tools already broadcasts reload to other tabs
    window.location.replace(window.location.origin)
  }

  Vue.prototype.$bus.$on('user-after-loggedin', onSsoLoggedIn)

  await rootStore.dispatch('user/startSession')
}
